import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { FlowQueryModel } from "src/app/models/flowquerymodel";
import {
  GUIDE_END_FORM_KEY,
  GuideQuestion,
  GuideQuestionResponse,
  isGuideQuestionFormActive,
  parseGuideQuestionForm,
} from "src/app/models/guide-question-form.model";
import { EventEmitterService } from "src/app/Services/event-emitter.service";
import { FlowServiceService } from "src/app/Services/flow-service.service";
import { FrontlineOpsService } from "src/app/Services/frontline-ops.service";
import { OrgContextService } from "src/app/Services/org-context.service";
import { OrganizationService } from "src/app/Services/organization.service";
import { PermissionsService } from "src/app/Services/permissions.service";
import { getLoggedInUser } from "src/app/shared/utils/plan.util";
import { QuestionResponsesComponent } from "../question-responses/question-responses.component";

type ReportTab = "runs" | "responses";

@Component({
  selector: "app-task-mode-reports",
  templateUrl: "./task-mode-reports.component.html",
  styleUrls: ["./task-mode-reports.component.scss"],
})
export class TaskModeReportsComponent implements OnInit {
  userData: any;
  flows: { id: string; title: string }[] = [];
  selectedFlowId = "";
  loadingFlows = false;
  loadingSessions = false;
  sessions: any[] = [];
  responses: GuideQuestionResponse[] = [];
  issues: any[] = [];
  requiredForms: { key: string; label: string; questions: GuideQuestion[] }[] = [];
  analytics: any = null;
  expandedRunId = "";
  toogleMenuClass = false;
  activeTab: ReportTab = "runs";

  @ViewChild(QuestionResponsesComponent) responsesView?: QuestionResponsesComponent;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private eventEmitterService: EventEmitterService,
    private flowService: FlowServiceService,
    private frontlineOps: FrontlineOpsService,
    private orgContext: OrgContextService,
    private organizationService: OrganizationService,
    private permissions: PermissionsService
  ) {}

  ngOnInit(): void {
    this.userData = getLoggedInUser();
    if (!this.userData) {
      this.router.navigate(["/"]);
      return;
    }
    if (!this.canAccess()) {
      this.toastr.warning(
        "Task Mode reports are available to Content Creators and organization / application / team admins."
      );
      this.router.navigate(["/user-dashboard/dashboard"]);
      return;
    }

    this.eventEmitterService.setDashboardMenuStyleProperty(
      "task-mode-reports,Task Mode Reports"
    );
    this.route.queryParamMap.subscribe((params) => {
      this.activeTab = params.get("tab") === "responses" ? "responses" : "runs";
    });
    this.loadFlows();
  }

  selectTab(tab: ReportTab): void {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tab === "runs" ? null : tab },
      queryParamsHandling: "merge",
      replaceUrl: true,
    });
  }

  canAccess(): boolean {
    const role = this.permissions.resolveEffectiveRole(this.userData);
    if (role === "Super Admin" || role === "Admin") return true;
    if (this.orgContext.isOrganizationMode()) {
      return this.orgContext.canViewUsersTab();
    }
    return role === "Content Creator";
  }

  private loadFlows(): void {
    this.loadingFlows = true;
    if (this.orgContext.isOrganizationMode()) {
      const orgId = this.orgContext.contentScope().organizationId;
      if (!orgId) {
        this.loadingFlows = false;
        this.flows = [];
        return;
      }
      this.organizationService.listAdminContent(orgId).subscribe({
        next: (rows) => {
          this.flows = (rows || [])
            .filter((r: any) => {
              const type = String(r.contentType || r.ContentType || "Guide").toLowerCase();
              return type === "guide" || type === "flow" || !type;
            })
            .map((r: any) => ({
              id: r.contentId || r.ContentId || r.rowKey || r.RowKey || "",
              title: r.title || r.Title || "Untitled guide",
            }))
            .filter((f: { id: string }) => !!f.id);
          this.loadingFlows = false;
          if (this.flows.length && !this.selectedFlowId) {
            this.selectedFlowId = this.flows[0].id;
            this.loadReport();
          }
        },
        error: () => {
          this.loadingFlows = false;
          this.flows = [];
          this.toastr.error("Could not load organization guides");
        },
      });
      return;
    }

    const flowQueryModel = new FlowQueryModel({
      userEmail: this.userData.emailAddress,
      otherUserEmail: this.userData.emailAddress,
      pageIndex: 0,
      category: "All",
    });
    this.flowService.getAllUserFlows(flowQueryModel).subscribe({
      next: (data: any) => {
        const list = data?.flows || data?.Flows || [];
        this.flows = (Array.isArray(list) ? list : []).map((f: any) => ({
          id: f.rowKey || f.RowKey || "",
          title: f.title || f.Title || "Untitled guide",
        })).filter((f: { id: string }) => !!f.id);
        this.loadingFlows = false;
        if (this.flows.length && !this.selectedFlowId) {
          this.selectedFlowId = this.flows[0].id;
          this.loadReport();
        }
      },
      error: () => {
        this.loadingFlows = false;
        this.flows = [];
        this.toastr.error("Could not load your guides");
      },
    });
  }

  onFlowChange(): void {
    this.loadReport();
  }

  refresh(): void {
    this.loadReport();
    this.responsesView?.loadReport();
  }

  loadReport(): void {
    if (!this.selectedFlowId) {
      this.sessions = [];
      this.analytics = null;
      return;
    }
    this.loadingSessions = true;
    this.analytics = null;
    this.sessions = [];
    this.responses = [];
    this.issues = [];
    this.requiredForms = [];
    this.expandedRunId = "";

    this.frontlineOps.getOpsAnalytics(this.selectedFlowId).subscribe({
      next: (a) => (this.analytics = a || null),
      error: () => (this.analytics = null),
    });

    this.flowService.getFlowById(this.selectedFlowId, this.userData?.emailAddress || "").subscribe({
      next: (data: any) => {
        const payload = data?.flows ?? data?.Flows ?? data;
        this.requiredForms = this.formsFromGuide(payload);
      },
      error: () => (this.requiredForms = []),
    });

    this.frontlineOps.listQuestionResponses(this.selectedFlowId).subscribe({
      next: (rows) => (this.responses = rows || []),
      error: () => (this.responses = []),
    });

    this.flowService.getFlowReviews({ FlowRowKey: this.selectedFlowId }).subscribe({
      next: (reviews: any) => {
        const list = Array.isArray(reviews) ? reviews : reviews?.reviews || reviews?.Reviews || [];
        this.issues = [];
        (list || []).forEach((review: any) => {
          const reviewIssues = review.issues || review.Issues || [];
          reviewIssues.forEach((issue: any) => this.issues.push(issue));
        });
      },
      error: () => (this.issues = []),
    });

    this.frontlineOps.listTaskSessions(this.selectedFlowId).subscribe({
      next: (rows) => {
        this.sessions = rows || [];
        this.loadingSessions = false;
      },
      error: (err) => {
        this.loadingSessions = false;
        this.sessions = [];
        this.toastr.error(err?.error?.error || "Could not load task sessions");
      },
    });
  }

  private formsFromGuide(flow: any): { key: string; label: string; questions: GuideQuestion[] }[] {
    if (!flow) return [];
    const steps = flow.steps || flow.Steps || [];
    const forms = (Array.isArray(steps) ? steps : []).map((step: any, index: number) => {
      const form = parseGuideQuestionForm(step.questionForm ?? step.QuestionForm);
      if (!isGuideQuestionFormActive(form)) return null;
      return {
        key: step.rowKey || step.RowKey || `step-${index}`,
        label: step.title || step.Title || `Step ${index + 1}`,
        questions: form!.questions,
      };
    }).filter((f): f is { key: string; label: string; questions: GuideQuestion[] } => !!f);
    const end = parseGuideQuestionForm(flow.endQuestionForm ?? flow.EndQuestionForm);
    if (isGuideQuestionFormActive(end)) {
      forms.push({ key: GUIDE_END_FORM_KEY, label: "End of guide", questions: end!.questions });
    }
    return forms;
  }

  runId(row: any): string {
    return row?.sessionId || row?.SessionId || row?.rowKey || row?.RowKey || "";
  }

  isRunComplete(row: any): boolean {
    const status = String(row?.status || row?.Status || "").toLowerCase();
    if (status === "completed") return true;
    if (status === "started") return false;
    const end = new Date(row?.endedAtUtc || row?.EndedAtUtc);
    return !isNaN(end.getTime()) && end.getFullYear() > 2000;
  }

  runStatus(row: any): string {
    return this.isRunComplete(row) ? "Recorded requirements satisfied" : "In progress";
  }

  private responsesFor(run: string): GuideQuestionResponse[] {
    return this.responses.filter((r) => (r.sessionId || "") === run);
  }

  private latestByForm(rows: GuideQuestionResponse[]): Map<string, GuideQuestionResponse> {
    const map = new Map<string, GuideQuestionResponse>();
    rows.forEach((r) => {
      const current = map.get(r.formKey);
      if (!current || new Date(r.submittedAtUtc || 0).getTime() >= new Date(current.submittedAtUtc || 0).getTime()) {
        map.set(r.formKey, r);
      }
    });
    return map;
  }

  get completionStats(): { completed: number; started: number; pct: number } {
    const started = this.sessions.length;
    const completed = this.sessions.filter((s) => this.isRunComplete(s)).length;
    return { completed, started, pct: started ? Math.round((completed / started) * 100) : 0 };
  }

  get coverageStats(): { valid: number; required: number; pct: number } {
    let required = 0;
    let valid = 0;
    this.sessions.forEach((session) => {
      const latest = this.latestByForm(this.responsesFor(this.runId(session)));
      this.requiredForms.forEach((form) => {
        const fields = form.questions.filter((q) => q.required);
        required += fields.length;
        const response = latest.get(form.key);
        if (!response || response.validationPassed === false) return;
        valid += fields.length;
      });
    });
    return { valid, required, pct: required ? Math.round((valid / required) * 100) : 0 };
  }

  get firstAttemptStats(): { passed: number; attempted: number; pct: number } {
    const groups = new Map<string, GuideQuestionResponse>();
    this.responses.forEach((r) => {
      if (!r.sessionId || !r.formKey) return;
      const key = `${r.sessionId}:${r.formKey}`;
      const current = groups.get(key);
      if (!current || new Date(r.submittedAtUtc || 0).getTime() < new Date(current.submittedAtUtc || 0).getTime()) {
        groups.set(key, r);
      }
    });
    const attempted = groups.size;
    const passed = Array.from(groups.values()).filter((r) => r.validationPassed !== false).length;
    return { passed, attempted, pct: attempted ? Math.round((passed / attempted) * 100) : 0 };
  }

  get failedTotal(): number {
    return this.failedSubmissions.reduce((sum, row) => sum + row.count, 0);
  }

  get failedSubmissions(): { label: string; worker: string; count: number }[] {
    const groups = new Map<string, { label: string; worker: string; count: number }>();
    this.responses.filter((r) => r.validationPassed === false).forEach((r) => {
      const label = r.stepTitle || r.formKey;
      const worker = r.userName || r.userEmail || "Unknown worker";
      const key = `${label}|${worker}`;
      const row = groups.get(key) || { label, worker, count: 0 };
      row.count += 1;
      groups.set(key, row);
    });
    return Array.from(groups.values());
  }

  get issueCounts(): { label: string; status: string; count: number }[] {
    const groups = new Map<string, { label: string; status: string; count: number }>();
    this.issues.forEach((issue) => {
      const label = issue.stepTitle || issue.StepTitle || "Step";
      const status = issue.resolutionStatus || issue.ResolutionStatus || issue.moderationStatus || issue.ModerationStatus || "Open";
      const key = `${label}|${status}`;
      const row = groups.get(key) || { label, status, count: 0 };
      row.count += 1;
      groups.set(key, row);
    });
    return Array.from(groups.values());
  }

  get unfinishedRuns(): any[] {
    return this.sessions.filter((s) => !this.isRunComplete(s));
  }

  lastStepLabel(row: any): string {
    const key = row?.lastCompletedFormKey || row?.LastCompletedFormKey;
    if (key === GUIDE_END_FORM_KEY) return "End of guide";
    const index = row?.lastCompletedStepIndex ?? row?.LastCompletedStepIndex;
    if (key) {
      const form = this.requiredForms.find((f) => f.key === key);
      if (form) return form.label;
    }
    if (typeof index === "number" && index >= 0) return `Step ${index + 1}`;
    return "Not started";
  }

  toggleRun(row: any): void {
    const id = this.runId(row);
    this.expandedRunId = this.expandedRunId === id ? "" : id;
  }

  attemptsFor(row: any): GuideQuestionResponse[] {
    return this.responsesFor(this.runId(row)).slice().sort(
      (a, b) => new Date(a.submittedAtUtc || 0).getTime() - new Date(b.submittedAtUtc || 0).getTime()
    );
  }

  issuesFor(row: any): any[] {
    const id = this.runId(row);
    return this.issues.filter((issue) => (issue.runId || issue.RunId) === id);
  }

  attemptResult(row: GuideQuestionResponse): string {
    if (row.validationPassed === false) return "Failed";
    if (row.validationPassed === true) return "Passed";
    return "Recorded";
  }

  attemptSummary(row: GuideQuestionResponse): string {
    return (row.answers || [])
      .map((a) => {
        const q = (row.questions || []).find((x) => x.id === a.questionId);
        const label = q?.text ? `${q.text}: ` : "";
        if (a.photoUrl) return `${label}Photo`;
        if (a.numberValue !== null && a.numberValue !== undefined) {
          return `${label}${a.numberValue}${q?.unit ? " " + q.unit : ""}`;
        }
        if (a.confirmed) return `${label}Confirmed`;
        if (a.text) return `${label}${a.text}`;
        if (a.optionIds?.length) {
          const chosen = a.optionIds.map((id) => q?.options?.find((o) => o.id === id)?.text || id);
          return `${label}${chosen.join(", ")}`;
        }
        return "";
      })
      .filter((part) => !!part)
      .join(" · ") || "No values";
  }

  photoUrls(row: GuideQuestionResponse): string[] {
    return (row.answers || []).map((a) => a.photoUrl || "").filter((url) => !!url);
  }

  formatDuration(seconds: number | null | undefined): string {
    const s = Math.max(0, Number(seconds) || 0);
    const m = Math.floor(s / 60);
    const rem = s % 60;
    if (m <= 0) return `${rem}s`;
    return `${m}m ${rem}s`;
  }

  formatDate(value: string | Date | null | undefined): string {
    if (!value) return "—";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  }

  sessionUser(row: any): string {
    return row?.userName || row?.UserName || "—";
  }

  sessionEmail(row: any): string {
    return row?.userEmail || row?.UserEmail || "—";
  }

  openGuide(): void {
    if (!this.selectedFlowId) return;
    const flow = this.flows.find((f) => f.id === this.selectedFlowId);
    const title = (flow?.title || "guide").replace(/\s+/g, "-");
    this.router.navigate(["/guide", title, this.selectedFlowId], {
      queryParams: { full: 1 },
    });
  }
}
